import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  averageShift: number;
  mostUsedShift: number;
  totalCharactersProcessed: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  shift: number;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
}

const CaesarCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [shift, setShift] = useState<number>(3);
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [includeNumbers, setIncludeNumbers] = useState<boolean>(false);
  const [includeSpecialChars, setIncludeSpecialChars] = useState<boolean>(false);
  const [customAlphabet, setCustomAlphabet] = useState<string>('');
  const [useCustomAlphabet, setUseCustomAlphabet] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    averageShift: 0,
    mostUsedShift: 3,
    totalCharactersProcessed: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);

  // Caesar cipher implementation
  const caesarCipher = useCallback((text: string, shiftValue: number, isEncrypt: boolean): string => {
    if (!text) return '';
    
    const actualShift = isEncrypt ? shiftValue : -shiftValue;
    
    if (useCustomAlphabet && customAlphabet) {
      return customAlphabetCipher(text, actualShift, customAlphabet);
    }
    
    return text.split('').map(char => {
      if (/[a-zA-Z]/.test(char)) {
        const isUpperCase = char === char.toUpperCase();
        const base = isUpperCase ? 65 : 97;
        const shifted = ((char.charCodeAt(0) - base + actualShift + 26) % 26) + base;
        const result = String.fromCharCode(shifted);
        return preserveCase ? result : (isUpperCase ? result.toLowerCase() : result);
      } else if (includeNumbers && /[0-9]/.test(char)) {
        return String.fromCharCode(((char.charCodeAt(0) - 48 + actualShift + 10) % 10) + 48);
      } else if (includeSpecialChars && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(char)) {
        const specialChars = '!@#$%^&*()_+-=[]{};\'":\\|,.<>/?';
        const index = specialChars.indexOf(char);
        if (index !== -1) {
          const newIndex = (index + actualShift + specialChars.length) % specialChars.length;
          return specialChars[newIndex];
        }
      }
      return char;
    }).join('');
  }, [preserveCase, includeNumbers, includeSpecialChars, useCustomAlphabet, customAlphabet]);

  const customAlphabetCipher = (text: string, shiftValue: number, alphabet: string): string => {
    return text.split('').map(char => {
      const index = alphabet.indexOf(char);
      if (index !== -1) {
        const newIndex = (index + shiftValue + alphabet.length) % alphabet.length;
        return alphabet[newIndex];
      }
      return char;
    }).join('');
  };

  // Process text
  const processText = useCallback(() => {
    const result = caesarCipher(inputText, shift, operation === 'encrypt');
    setOutputText(result);
    
    // Update stats and history
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      shift,
      operation,
      timestamp: new Date()
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]); // Keep last 50 items
    
    setStats(prev => ({
      ...prev,
      totalEncryptions: prev.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
      totalDecryptions: prev.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
      totalCharactersProcessed: prev.totalCharactersProcessed + inputText.length,
      averageShift: Math.round((prev.averageShift + shift) / 2),
      mostUsedShift: shift // Simplified logic
    }));
  }, [inputText, shift, operation, caesarCipher]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText) {
      processText();
    } else {
      setOutputText('');
    }
  }, [inputText, shift, operation, processText]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setShift(3);
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <>
    <Helmet>
        {/* Basic SEO */}
        <title>Caesar Cipher - Advanced Encryption & Decryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Advanced Caesar Cipher tool with real-time encryption/decryption, custom alphabets, analytics, and dark mode. Perfect for cryptography learning, password encoding, and text security." />
        <meta name="keywords" content="caesar cipher, encryption, decryption, cryptography, cipher tool, text encryption, password encoder, cryptanalysis, security tool, online cipher" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Caesar Cipher - Advanced Encryption & Decryption Tool" />
        <meta property="og:description" content="Professional Caesar Cipher tool with advanced features including custom alphabets, analytics, and real-time processing. Free online cryptography tool." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Caesar Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Caesar Cipher - Advanced Encryption Tool" />
        <meta name="twitter:description" content="Professional Caesar Cipher with advanced features, analytics, and real-time processing. Free cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@caesarcipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Caesar Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Caesar Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Caesar Cipher Tool",
            "description": "Advanced Caesar Cipher encryption and decryption tool with real-time processing, analytics, and custom features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": "SecurityApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time encryption and decryption",
              "Custom alphabet support",
              "Analytics and statistics",
              "Dark and light themes",
              "File upload and download",
              "Fullscreen mode",
              "Mobile responsive"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Caesar Cipher Tool"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1247",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
      </Helmet>
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Caesar Cipher</h1>
          
          <div className="flex items-center space-x-2">
            {/* Advanced Options */}
            <button
              onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Advanced Options"
            >
              <Settings size={20} />
            </button>
            
            {/* Analytics */}
            <button
              onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Analytics"
            >
              <BarChart3 size={20} />
            </button>
            
            {/* Theme Toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Toggle Theme"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            
            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Toggle Fullscreen"
            >
              {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          
          {/* Controls */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">Operation</label>
                <select
                  value={operation}
                  onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                  className={`w-full p-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="encrypt">Encrypt</option>
                  <option value="decrypt">Decrypt</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Shift Value</label>
                <input
                  type="number"
                  value={shift}
                  onChange={(e) => setShift(Number(e.target.value))}
                  min="1"
                  max="25"
                  className={`w-full p-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={resetAll}
                  className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                  } transition-colors`}
                >
                  <RotateCcw size={16} />
                  <span>Reset</span>
                </button>
              </div>
              
              <div className="flex items-end">
                <label className={`w-full p-2 rounded border cursor-pointer flex items-center justify-center space-x-2 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                } transition-colors`}>
                  <Upload size={16} />
                  <span>Upload File</span>
                  <input
                    type="file"
                    accept=".txt"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Text Areas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Input Text</h3>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text to encrypt or decrypt..."
                className={`w-full h-64 p-4 rounded border resize-none ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                }`}
              />
            </div>

            {/* Output */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Output Text</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => copyToClipboard(outputText)}
                    className={`p-2 rounded ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    } transition-colors`}
                    title="Copy to clipboard"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={() => downloadText(outputText, 'caesar-cipher-output.txt')}
                    className={`p-2 rounded ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    } transition-colors`}
                    title="Download as file"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </div>
              <textarea
                value={outputText}
                readOnly
                className={`w-full h-64 p-4 rounded border resize-none ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Settings Modal */}
      {activeModal === 'settings' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Advanced Options</h2>
              <button
                onClick={() => setActiveModal(null)}
                className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Preserve Case</label>
                <input
                  type="checkbox"
                  checked={preserveCase}
                  onChange={(e) => setPreserveCase(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Include Numbers</label>
                <input
                  type="checkbox"
                  checked={includeNumbers}
                  onChange={(e) => setIncludeNumbers(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Include Special Characters</label>
                <input
                  type="checkbox"
                  checked={includeSpecialChars}
                  onChange={(e) => setIncludeSpecialChars(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Use Custom Alphabet</label>
                <input
                  type="checkbox"
                  checked={useCustomAlphabet}
                  onChange={(e) => setUseCustomAlphabet(e.target.checked)}
                  className="rounded"
                />
              </div>
              
              {useCustomAlphabet && (
                <div>
                  <label className="block text-sm font-medium mb-2">Custom Alphabet</label>
                  <input
                    type="text"
                    value={customAlphabet}
                    onChange={(e) => setCustomAlphabet(e.target.value)}
                    placeholder="Enter custom character set..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {activeModal === 'analytics' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Analytics</h2>
              <button
                onClick={() => setActiveModal(null)}
                className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                <div className="text-sm">Total Encryptions</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                <div className="text-sm">Total Decryptions</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-purple-500">{stats.averageShift}</div>
                <div className="text-sm">Average Shift</div>
              </div>
              <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                <div className="text-sm">Characters Processed</div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Recent History</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.length > 0 ? history.slice(0, 10).map(item => (
                  <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs px-2 py-1 rounded ${
                        item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {item.operation.toUpperCase()}
                      </span>
                      <span className="text-xs opacity-60">
                        Shift: {item.shift}
                      </span>
                    </div>
                    <div className="text-sm">
                      <div className="truncate">{item.input}</div>
                      <div className="truncate opacity-60">→ {item.output}</div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 opacity-60">
                    No operations performed yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default CaesarCipherApp;